import {
  CardActions,
  CardContent as MuiCardContentWrapper,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { CardStyled } from './PageContentCardStyled';
import defaultPageContentCardImage from 'theSource/assets/background.png';
import { PageContentCardProps } from './types';
import { CardContent } from './CardContent';
import { FullPageCardContent } from './FullPageCardContent';

export const PageContentCard = ({
  slotNum,
  iconImage,
  content,
  label,
  extraClass,
  documents,
  url,
  urlType,
  fullContentCard = false,
}: PageContentCardProps) => {
  const { t } = useTranslation('pages.openEnrollment');
  const contentRef = useRef<HTMLDivElement>(null);
  const [overLineClampLimit, setOverLineClampLimit] = useState<boolean>(false);
  const [objectFitStyle, setObjectFitStyle] = useState<'cover' | 'none'>(
    'cover'
  );
  const imageRef = useRef<HTMLImageElement>(null);

  const isSingleClass = extraClass?.includes('single') || false;

  useEffect(() => {
    if (contentRef && contentRef.current) {
      if (contentRef.current.scrollHeight > contentRef.current.clientHeight) {
        setOverLineClampLimit(true);
      }
    }
  }, [contentRef]);

  useEffect(() => {
    if (isSingleClass && imageRef.current) {
      const img = imageRef.current;
      const containerWidth = img.parentElement?.offsetWidth || 0;

      const containerHeight = img.parentElement?.offsetHeight || 0;

      if (
        img.naturalWidth < containerWidth ||
        img.naturalHeight < containerHeight
      ) {
        setObjectFitStyle('none'); // Smaller images: center with white space
      } else {
        setObjectFitStyle('cover'); // Larger images: scale to fit
      }
    }
  }, [extraClass]);

  const showLearnMoreDocs =
    !content && documents && documents.length > 3 ? true : false;

  const showLearnMoreLink =
    (overLineClampLimit || showLearnMoreDocs || url) && !fullContentCard;

  const cleanLabel = label?.replace(/<\/?[^>]+>/g, '');
  return (
    <CardStyled
      id={String(slotNum)}
      sx={{ maxHeight: '50rem' }}
      tabIndex={0}
      className={`page-content-card ${extraClass || ''}`}
      data-testid="page-content-card"
    >
      <div className="image-container">
        <img
          ref={imageRef}
          alt={label || ''}
          src={iconImage ? iconImage : defaultPageContentCardImage}
          aria-hidden="true"
          className="page-content-card-image"
          style={objectFitStyle ? { objectFit: objectFitStyle } : undefined}
        />
      </div>
      <MuiCardContentWrapper className="main-content">
        {!fullContentCard ? (
          <CardContent
            label={cleanLabel}
            content={content}
            contentRef={contentRef}
            documents={documents}
          />
        ) : (
          <FullPageCardContent
            label={cleanLabel}
            content={content}
            documents={documents}
            url={url}
            urlType={urlType}
          />
        )}
      </MuiCardContentWrapper>
      {showLearnMoreLink && (
        <CardActions>
          <Link data-testid="learn-more-link" to={`/info/${slotNum}`}>
            {t('benefitsInformation.learnMore')}
          </Link>
        </CardActions>
      )}
    </CardStyled>
  );
};
